import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ArrowLeftRight, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import {
  AmRechnerBearbeiten,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DataTable,
  EmptyState,
  IconButton,
  NativeSelect,
  Pagination,
  StatTile,
  Tooltip,
  cn,
  formatDatum,
  formatEuro,
  toast,
  useMobil,
  type Column,
} from '@elbklar/ui';

import { SeitenKopf } from '@/components/layout/SeitenKopf';
import { SelectField, TextField } from '@/components/Felder';
import { TransactionDialog } from './TransactionDialog';
import { categoriesApi, transactionsApi } from '@/lib/api';
import { satzText } from '@/lib/steuer';
import type { Transaction, TransactionFilters } from '@/types';

const SEITENGROESSE = 50;

/**
 * Buchungen — Ist-Daten (U3): schlicht, am Rechner. Am Handy steht die Liste
 * zum Nachsehen da, ohne Filter, Zuordnung und Aktionen.
 */
export function BuchungenPage() {
  const queryClient = useQueryClient();
  const mobil = useMobil();
  const [suchparameter] = useSearchParams();

  const [filter, setFilter] = useState<TransactionFilters>({
    limit: SEITENGROESSE,
    offset: 0,
  });
  const [dialogOffen, setDialogOffen] = useState(false);
  const [inBearbeitung, setInBearbeitung] = useState<Transaction | null>(null);
  const [zuLoeschen, setZuLoeschen] = useState<Transaction | null>(null);

  // Der Verweis von der Uebersicht kommt mit ?uncategorized=1 herein.
  useEffect(() => {
    if (suchparameter.get('uncategorized') === '1') {
      setFilter((vorher) => ({ ...vorher, uncategorized: true, offset: 0 }));
    }
  }, [suchparameter]);

  const buchungen = useQuery({
    queryKey: ['transactions', filter],
    queryFn: () => transactionsApi.list(filter),
  });

  const kennzahlen = useQuery({
    queryKey: ['transactions-summary', filter],
    queryFn: () => {
      const { offset, limit, ...rest } = filter;
      void offset;
      void limit;
      return transactionsApi.summary(rest);
    },
  });

  const kategorien = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.list(),
  });

  const aktualisieren = () => {
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions-summary'] });
    void queryClient.invalidateQueries({ queryKey: ['overview'] });
  };

  const kategorieSetzen = useMutation({
    mutationFn: ({ id, kategorie }: { id: string; kategorie: string | null }) =>
      transactionsApi.update(id, { category_id: kategorie }),
    onSuccess: aktualisieren,
    onError: () => toast.error('Kategorie konnte nicht gesetzt werden'),
  });

  // Der Fehler erscheint im Bestätigungsdialog, der dafür offen bleibt.
  const loeschen = useMutation({
    mutationFn: (id: string) => transactionsApi.remove(id),
    onSuccess: () => {
      toast.info('Buchung gelöscht');
      aktualisieren();
    },
  });

  const oeffneDialog = (buchung: Transaction | null) => {
    setInBearbeitung(buchung);
    setDialogOffen(true);
  };

  const setzeFilter = (teil: Partial<TransactionFilters>) => setFilter((vorher) => ({ ...vorher, ...teil, offset: 0 }));

  const daten = buchungen.data;
  // Netto und Brutto nur trennen, wenn es etwas zu trennen gibt — sonst
  // stuenden ueberall zwei identische Zahlen.
  const mitSteuer = Number(kennzahlen.data?.tax_total ?? 0) !== 0;
  const seite = Math.floor((filter.offset ?? 0) / SEITENGROESSE) + 1;
  const seiten = daten ? Math.max(1, Math.ceil(daten.total / SEITENGROESSE)) : 1;

  const kategorieOptionen = (kategorien.data ?? []).map((k) => ({ value: k.id, label: k.name }));
  const kategorieName = (id: string | null) => (id ? kategorien.data?.find((k) => k.id === id)?.name : undefined);

  const spalten: Column<Transaction>[] = [
    { key: 'booking_date', header: 'Datum', mono: true, mobil: 'unter', render: (b) => formatDatum(b.booking_date) },
    {
      key: 'counterparty',
      header: 'Empfänger',
      strong: true,
      mobil: 'titel',
      render: (b) => <span className="block max-w-[200px] truncate">{b.counterparty ?? '–'}</span>,
      renderMobil: (b) => b.counterparty ?? b.purpose ?? '–',
    },
    {
      key: 'purpose',
      header: 'Verwendungszweck',
      render: (b) => <span className="block max-w-[280px] truncate text-muted-foreground">{b.purpose ?? '–'}</span>,
    },
    {
      key: 'category_id',
      header: 'Kategorie',
      mobil: 'status',
      width: 176,
      render: (b) => (
        <NativeSelect
          aria-label={`Kategorie für ${b.counterparty ?? b.purpose ?? 'Buchung'}`}
          value={b.category_id ?? ''}
          options={[{ value: '', label: 'offen' }, ...kategorieOptionen]}
          onChange={(e) => kategorieSetzen.mutate({ id: b.id, kategorie: e.target.value || null })}
          className="w-40"
        />
      ),
      renderMobil: (b) => kategorieName(b.category_id) ?? <span className="text-warning">offen</span>,
    },
    {
      key: 'amount',
      header: 'Betrag',
      align: 'end',
      mono: true,
      mobil: 'wert',
      render: (b) => (
        <span className={cn('font-medium', Number(b.amount) < 0 ? 'text-foreground' : 'text-success')}>
          {formatEuro(b.amount, { cent: true })}
        </span>
      ),
    },
    {
      key: 'tax_rate',
      header: 'USt.',
      align: 'end',
      mono: true,
      render: (b) =>
        Number(b.tax_rate) > 0 ? (
          <Tooltip content={`${formatEuro(b.net_amount, { cent: true })} netto`}>
            <span>{satzText(b.tax_rate)}</span>
          </Tooltip>
        ) : (
          satzText(b.tax_rate)
        ),
    },
    {
      key: 'aktionen',
      header: '',
      align: 'end',
      width: 88,
      render: (b) => (
        <div className="flex justify-end gap-1">
          <IconButton icon={Pencil} label="Bearbeiten" variant="ghost" size="sm" onClick={() => oeffneDialog(b)} />
          <IconButton icon={Trash2} label="Löschen" variant="ghost" size="sm" onClick={() => setZuLoeschen(b)} />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <SeitenKopf
        title="Buchungen"
        subtitle="Alle tatsächlichen Zu- und Abflüsse aus Import und manueller Erfassung."
        actions={
          !mobil && (
            <Button variant="primary" icon={Plus} onClick={() => oeffneDialog(null)}>
              Buchung erfassen
            </Button>
          )
        }
      />

      <AmRechnerBearbeiten className="md:hidden">
        Buchungen erfassen, zuordnen und filtern geht am Rechner.
      </AmRechnerBearbeiten>

      {kennzahlen.data && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile
            label="Eingang"
            value={<span className="text-success">{formatEuro(kennzahlen.data.total_in, { cent: true })}</span>}
            hint={mitSteuer ? `${formatEuro(kennzahlen.data.total_in_net, { cent: true })} netto` : undefined}
          />
          <StatTile
            label="Ausgang"
            value={formatEuro(kennzahlen.data.total_out, { cent: true })}
            hint={mitSteuer ? `${formatEuro(kennzahlen.data.total_out_net, { cent: true })} netto` : undefined}
          />
          <StatTile
            label="Saldo"
            value={formatEuro(kennzahlen.data.net, { cent: true })}
            hint={mitSteuer ? `davon ${formatEuro(kennzahlen.data.tax_total, { cent: true })} Umsatzsteuer` : undefined}
          />
          <StatTile
            label="Ohne Kategorie"
            value={
              kennzahlen.data.uncategorized > 0 ? (
                <span className="text-warning">{kennzahlen.data.uncategorized}</span>
              ) : (
                kennzahlen.data.uncategorized
              )
            }
          />
        </div>
      )}

      <Card className="max-md:hidden">
        <div className="grid grid-cols-1 items-end gap-3 md:grid-cols-2 lg:grid-cols-5">
          <TextField
            label="Von"
            type="date"
            value={filter.von ?? ''}
            onChange={(e) => setzeFilter({ von: e.target.value || undefined })}
          />
          <TextField
            label="Bis"
            type="date"
            value={filter.bis ?? ''}
            onChange={(e) => setzeFilter({ bis: e.target.value || undefined })}
          />
          <SelectField
            label="Kategorie"
            options={[{ value: '', label: 'alle' }, ...kategorieOptionen]}
            value={filter.category_id ?? ''}
            onChange={(e) =>
              setzeFilter({
                category_id: e.target.value || undefined,
                uncategorized: false,
              })
            }
          />
          <TextField
            label="Suche"
            icon={Search}
            placeholder="Empfänger oder Zweck"
            value={filter.suche ?? ''}
            onChange={(e) => setzeFilter({ suche: e.target.value || undefined })}
          />
          <div className="flex h-control-md items-center gap-3">
            <Checkbox
              label="Nur offene"
              checked={filter.uncategorized ?? false}
              onChange={(an) => setzeFilter({ uncategorized: an, category_id: undefined })}
            />
            <Button variant="ghost" size="sm" onClick={() => setFilter({ limit: SEITENGROESSE, offset: 0 })}>
              Filter zurücksetzen
            </Button>
          </div>
        </div>
      </Card>

      <section className="space-y-2">
        {daten && daten.total > 0 && <p className="t-small text-muted-foreground">{daten.total} Buchungen</p>}
        <DataTable
          columns={spalten}
          rows={daten?.items ?? []}
          loading={buchungen.isLoading}
          label="Buchungen"
          empty={
            <EmptyState
              icon={ArrowLeftRight}
              title="Keine Buchungen"
              description="Buchungen aus Import und Erfassung erscheinen hier; zum Filter passt gerade keine."
            />
          }
        />
        {daten && daten.total > SEITENGROESSE && (
          <Pagination
            page={seite}
            pageCount={seiten}
            onPageChange={(neu) => setFilter((v) => ({ ...v, offset: (neu - 1) * SEITENGROESSE }))}
          />
        )}
      </section>

      <TransactionDialog
        offen={dialogOffen}
        onSchliessen={() => setDialogOffen(false)}
        kategorien={kategorien.data ?? []}
        onGespeichert={aktualisieren}
        buchung={inBearbeitung}
      />

      <ConfirmDialog
        open={zuLoeschen !== null}
        onOpenChange={(offen) => !offen && setZuLoeschen(null)}
        title="Buchung löschen"
        description={
          zuLoeschen
            ? `${formatDatum(zuLoeschen.booking_date)} · ${zuLoeschen.counterparty ?? zuLoeschen.purpose ?? 'ohne Empfänger'} · ${formatEuro(
                zuLoeschen.amount,
                { cent: true }
              )}. Die Buchung fällt aus Auswertung und Ist-Burn.`
            : undefined
        }
        confirmLabel="Buchung löschen"
        tone="danger"
        onConfirm={() => (zuLoeschen ? loeschen.mutateAsync(zuLoeschen.id) : undefined)}
      />
    </div>
  );
}
